import React from "react";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

const SelectionGroup = ({
    label,
    options = [],
    className = "",
    itemClassName = "",
    errorClassName = "",
    isReadOnly = false,
    mutuallyExclusive = true,
    errorNames = [], // Array of field names to check for errors
}) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();

    const handleCheckboxChange = (name) => {
        if (isReadOnly) return;

        const currentValue = watch(name);
        const newValue = !currentValue;

        setValue(name, newValue, { shouldValidate: true, shouldDirty: true });

        if (mutuallyExclusive && newValue) {
            // If we are checking this one, uncheck all others in the group
            options.forEach((option) => {
                if (option.name !== name) {
                    setValue(option.name, false, { shouldValidate: true, shouldDirty: true });
                }
            });
        }
    };

    // Find if any of the fields in this group have errors
    const errorFound = errorNames.find((name) => {
        const parts = name.split(".");
        return parts.reduce((acc, part) => acc && acc[part], errors);
    });

    const errorMessage = errorFound
        ? errorNames.map(name => {
            const parts = name.split(".");
            return parts.reduce((acc, part) => acc && acc[part], errors);
        }).find(e => e)?.message
        : null;

    return (
        <div className={`space-y-4 ${className}`}>
            {label && (
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    {label}
                </label>
            )}

            <div className={`space-y-3 ${itemClassName}`}>
                {options.map((option) => (
                    <label
                        key={option.name}
                        className={`flex items-center space-x-3 group ${isReadOnly ? "cursor-default" : "cursor-pointer"
                            }`}
                    >
                        <input
                            type="checkbox"
                            checked={watch(option.name) || false}
                            onChange={() => handleCheckboxChange(option.name)}
                            disabled={isReadOnly}
                            className={`w-5 h-5 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 transition-all ${watch(option.name)
                                    ? "bg-blue-600 border-transparent"
                                    : "bg-white"
                                } ${isReadOnly ? "cursor-not-allowed opacity-70" : ""}`}
                        />
                        <span
                            className={`text-sm font-medium transition-colors ${isReadOnly ? "text-gray-500" : "text-gray-700 group-hover:text-gray-900"
                                }`}
                        >
                            {option.label}
                        </span>
                    </label>
                ))}
            </div>

            {errorMessage && (
                <div className={`mt-2 ${errorClassName}`}>
                    <p className="text-red-500 text-sm font-medium bg-red-50 p-2 rounded inline-block">
                        {errorMessage}
                    </p>
                </div>
            )}
        </div>
    );
};

SelectionGroup.propTypes = {
    label: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    errorClassName: PropTypes.string,
    isReadOnly: PropTypes.bool,
    mutuallyExclusive: PropTypes.bool,
    errorNames: PropTypes.arrayOf(PropTypes.string),
};

export default SelectionGroup;
